import React, { useCallback, useMemo } from 'react';
import type { ExtendedComponentSchema } from 'formiojs';
import { Components } from 'formiojs';
import type {
  EhsFormioReactProps,
  FormioBuilderInfo,
  FormioBuilderSchema
} from '../';
import { EhsFormioReactComponent } from '../';
import { Checkbox } from '../../../form';
import type { Component } from '@formio/react';

//START-EHS-REACT-FORMIO-DEFINITION---------------------------------------------
export type EhsFormioCheckboxConstructor = {
  classNames: string;
};
/**
 * @param classNames a string separated by spaces that will be added as classNames
 */
export type EhsFormioCheckboxSettings = {
  classNames?: string;
};
export const EhsFormioCheckboxType = 'ehsCheckbox';
export class EhsFormioCheckbox extends EhsFormioReactComponent<
  EhsFormioCheckboxSettings,
  EhsFormioCheckboxConstructor
> {
  /**
   * defines the default schema of the custom component
   * https://help.form.io/developers/custom-components#component-methods
   */
  static schema(
    extend?: FormioBuilderSchema
  ): ExtendedComponentSchema<FormioBuilderSchema> {
    return Components.components.field.schema({
      type: EhsFormioCheckboxType,
      label: 'common.unknown',
      key: EhsFormioCheckboxType,
      classNames: '',
      ...extend
    });
  }
  /**
   * defines how the component will look in the Form Editor (Builder)
   * https://help.form.io/developers/custom-components#component-methods
   */
  static get builderInfo(): FormioBuilderInfo {
    return {
      title: 'Ehs-Checkbox',
      group: 'advanced',
      icon: 'check-square',
      documentation: '/developers/custom-components',
      weight: 40,
      schema: EhsFormioCheckbox.schema()
    };
  }

  /**
   * defines the fields available in the Editor/Builder
   * https://help.form.io/developers/form-builder#overriding-behavior
   */
  static editForm() {
    return {
      components: [
        {
          type: 'textfield',
          key: 'label',
          label: 'label',
          tooltip: 'Please add only valid translation keys'
        },
        {
          type: 'textfield',
          key: 'classNames',
          label: 'classNames',
          tooltip:
            ' A string separated by spaces that will be added as classNames',
          weight: 20
        }
      ]
    };
  }

  //Begin Non static definitions-------------------------------------
  private classNames = '';
  extractFields(component: Component<EhsFormioCheckboxConstructor>): void {
    this.classNames = component.classNames;
    this.shouldSetValue = true;
    this.dataForSetting = this._data[component.key as string];
  }

  attachEhsReact(): [
    (props: EhsFormioReactProps<EhsFormioCheckboxSettings>) => JSX.Element,
    EhsFormioCheckboxSettings
  ] {
    return [
      CheckboxCmp,
      {
        classNames: this.classNames
      }
    ];
  }
}
//END-EHS-REACT-FORMIO-DEFINITION-----------------------------------------------

/**
 * The Logic for the Formio-Component.
 *
 * @param params formio {@link EhsFormioCheckboxSettings} properties
 */
function CheckboxCmp({
  value,
  onChange,
  label,
  readOnly,
  settings
}: EhsFormioReactProps<EhsFormioCheckboxSettings>): JSX.Element {
  const onChangeWrapper = useCallback(
    (val: boolean): void => {
      onChange(val.toString());
    },
    [onChange]
  );
  const wrapperValue = useMemo(() => {
    return (value ?? undefined) == 'true';
  }, [value]);
  return useMemo(() => {
    return (
      <Checkbox
        onChange={onChangeWrapper}
        value={wrapperValue}
        label={label}
        disabled={readOnly}
        className={settings.classNames}
      />
    );
  }, [label, onChangeWrapper, readOnly, settings.classNames, wrapperValue]);
}
